import React, { useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useGetOrderQuery } from '../services/api';
import { clearCart } from '../store/cartSlice';
import { medusaApi } from '../services/api';
import { Order } from '../types';
import { useTheme } from '../themes/ThemeProvider';
import { Icon } from './checkout/icons';

interface LocationState {
  order?: Order;
}

const OrderConfirmation: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation();
  const dispatch = useDispatch();
  const { branding } = useTheme();
  const confirmationIconName = branding?.checkout?.confirmationIcon || 'check-circle';

  const hasCleared = useRef(false);

  // Get order from navigation state (passed from OrderReview after checkout)
  const stateOrder = (location.state as LocationState)?.order;

  // Only fetch from API if we don't have the order in state
  // Note: The store API requires authentication, so this will only work for logged-in customers
  const {
    data: orderData,
    isLoading,
    error,
  } = useGetOrderQuery(orderId || '', {
    skip: !orderId || !!stateOrder, // Skip API call if we have the order in state
  });

  // Use order from state first, fallback to API data
  const order = useMemo(() => {
    const rawOrder = stateOrder || orderData?.order;
    // Log order data for debugging
    console.log('[OrderConfirmation] Order data:', rawOrder);
    return rawOrder;
  }, [stateOrder, orderData]);

  // Validate order has minimum required fields
  const isValidOrder = useMemo(() => {
    if (!order) return false;
    // Check that order has at least an id and isn't an error object
    if (typeof order !== 'object') return false;
    if ('message' in order && 'name' in order && 'type' in order && !('id' in order)) {
      // This looks like an Error object, not an order
      console.error('[OrderConfirmation] Order appears to be an error object:', order);
      return false;
    }
    return !!order.id;
  }, [order]);

  // Clear the cart only when we have a verified order (once only).
  // Gate on `order && isValidOrder` to prevent stale/bookmarked confirmation
  // links from clearing an active cart. Order data comes from either:
  // - location.state (passed from checkout — proves order was just placed)
  // - GET /store/orders/:id API (confirms order exists in backend)
  useEffect(() => {
    if (order && isValidOrder && !hasCleared.current) {
      hasCleared.current = true;
      console.log('[OrderConfirmation] Clearing cart for verified order:', order.id);
      dispatch(clearCart());
      dispatch(medusaApi.util.resetApiState());
    }
  }, [order, isValidOrder, dispatch]);

  const formatPrice = (amount: number | undefined | null, currency: string | undefined | null) => {
    try {
      const safeAmount = typeof amount === 'number' ? amount : 0;
      const safeCurrency = typeof currency === 'string' ? currency : 'sek';
      const locale = safeCurrency.toLowerCase() === 'sek' ? 'sv-SE' : 'en-US';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: safeCurrency.toUpperCase(),
      }).format(safeAmount / 100);
    } catch (e) {
      console.error('[OrderConfirmation] formatPrice error:', e);
      return 'N/A';
    }
  };

  const formatAddress = (address: any) => {
    if (!address || typeof address !== 'object') return 'Address not available';

    try {
      const safeStr = (val: any) => (typeof val === 'string' ? val : '');
      const parts = [
        safeStr(address.first_name) + ' ' + safeStr(address.last_name),
        safeStr(address.company),
        safeStr(address.address_1),
        safeStr(address.address_2),
        `${safeStr(address.city)}, ${safeStr(address.province)} ${safeStr(address.postal_code)}`,
        safeStr(address.country_code),
      ].filter(Boolean);

      return parts.join('\n') || 'Address not available';
    } catch (e) {
      console.error('[OrderConfirmation] formatAddress error:', e);
      return 'Address not available';
    }
  };

  // Only show loading if we're fetching from API and don't have order from state
  if (isLoading && !order) {
    return (
      <div className="order-confirmation">
        <div className="loading">Loading order details...</div>
      </div>
    );
  }

  // Show error only if both state and API failed to provide order, or order is invalid
  if ((error && !stateOrder) || !order || !isValidOrder) {
    const errorMessage = order && !isValidOrder
      ? 'There was an issue processing your order. Please contact support.'
      : "We couldn't find your order. Please check your order number and try again.";
    return (
      <div className="order-confirmation">
        <div className="error">
          <h2>Order Issue</h2>
          <p>{errorMessage}</p>
          <Link to="/" className="btn btn-primary">
            Return to Store
          </Link>
        </div>
      </div>
    );
  }

  // Safely get string values from order (handles objects being returned instead of primitives)
  const safeString = (value: any, fallback: string = ''): string => {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    return fallback;
  };

  // Wrap render in try-catch to handle any unexpected data structure issues
  try {
    // Additional validation before rendering
    if (!order.id) {
      console.error('[OrderConfirmation] Order has no id:', order);
      throw new Error('Invalid order: missing id');
    }
  } catch (e) {
    console.error('[OrderConfirmation] Validation error:', e);
    return (
      <div className="order-confirmation">
        <div className="error">
          <h2>Order Processing</h2>
          <p>Your order is being processed. Please check your email for confirmation.</p>
          <Link to="/" className="btn btn-primary">
            Return to Store
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="order-confirmation">
      <div className="confirmation-header">
        <div className="success-icon" aria-hidden="true" data-testid="order-confirmation-icon">
          <Icon name={confirmationIconName} size={56} />
        </div>
        <h1>Order Confirmed!</h1>
        <p className="confirmation-message">
          Thank you for your order. We'll send you a confirmation email shortly.
        </p>
      </div>

      <div className="order-details">
        <div className="order-info">
          <h2>Order #{order.display_id || order.id}</h2>
          <div className="order-meta">
            <p><strong>Order Date:</strong> {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</p>
            <p><strong>Email:</strong> {safeString(order.email, 'N/A')}</p>
            <p><strong>Status:</strong> {safeString(order.status, 'Confirmed')}</p>
          </div>
        </div>

        <div className="order-sections">
          {/* Shipping Information */}
          <div className="order-section">
            <h3>Shipping Address</h3>
            <div className="address-info">
              <pre>{formatAddress(order.shipping_address)}</pre>
            </div>
          </div>

          {/* Billing Information */}
          <div className="order-section">
            <h3>Billing Address</h3>
            <div className="address-info">
              <pre>{formatAddress(order.billing_address)}</pre>
            </div>
          </div>

          {/* Order Items */}
          <div className="order-section">
            <h3>Order Items</h3>
            <div className="order-items">
              {Array.isArray(order.items) && order.items.map((item, index) => (
                <div key={item?.id || `item-${index}`} className="order-item">
                  <div className="item-details">
                    <h4>{safeString(item?.product?.title || item?.title || (item as any)?.variant_title, 'Product')}</h4>
                    {item?.variant?.title && typeof item.variant.title === 'string' && (
                      <p className="variant-title">{item.variant.title}</p>
                    )}
                    <p className="item-quantity">Quantity: {item?.quantity || 1}</p>
                  </div>
                  <div className="item-price">
                    {formatPrice((item?.unit_price || 0) * (item?.quantity || 1), order.currency_code || 'sek')}
                  </div>
                </div>
              ))}
              {(!order.items || !Array.isArray(order.items) || order.items.length === 0) && (
                <p>Order details are being processed...</p>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="order-section">
            <h3>Order Summary</h3>
            <div className="order-totals">
              <div className="total-line">
                <span>Subtotal</span>
                <span>{formatPrice(order.total - (order.shipping_total || 0) - (order.tax_total || 0), order.currency_code)}</span>
              </div>
              <div className="total-line">
                <span>Shipping</span>
                <span>
                  {(order.shipping_total || 0) === 0 
                    ? 'FREE' 
                    : formatPrice(order.shipping_total || 0, order.currency_code)
                  }
                </span>
              </div>
              <div className="total-line">
                <span>Tax</span>
                <span>{formatPrice(order.tax_total || 0, order.currency_code)}</span>
              </div>
              <div className="total-line total-final">
                <span><strong>Total</strong></span>
                <span><strong>{formatPrice(order.total, order.currency_code)}</strong></span>
              </div>
            </div>
          </div>
        </div>

        <div className="next-steps">
          <h3>What's Next?</h3>
          <ul>
            <li>You'll receive an order confirmation email at {safeString(order.email, 'your email address')}</li>
            <li>We'll send you tracking information once your order ships</li>
            <li>Estimated delivery time: 3-7 business days</li>
          </ul>
        </div>

        <div className="confirmation-actions">
          <Link to="/" className="btn btn-primary">
            Continue Shopping
          </Link>
          <button 
            onClick={() => window.print()} 
            className="btn btn-secondary"
          >
            Print Order
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;